/**
 * The **table scene** — what the GM is showing the table on the stage (`decisions/0203` §2).
 * Two kinds: `narrative` (theatre of the mind; the roll pad) and `encounter` (that
 * encounter's map, before, during and after the fight). More kinds wait for a need.
 *
 * Named "table scene", not bare "scene", because "scene" already means the terrain-preset
 * library (`lvtt encounter scene save|load`, `<world>/scenes/`) and the "1/scene" rate grain.
 *
 * It replaced the old `activeEncounterId` pointer: an encounter on the table is the live fight
 * players are admitted to, so {@link tableSceneEncounterId} is that pointer's derivation.
 */
export type TableScene =
  | { kind: "narrative" }
  | { kind: "encounter"; encounterId: string };

export const NARRATIVE_SCENE: TableScene = { kind: "narrative" };

export function encounterScene(encounterId: string): TableScene {
  return { kind: "encounter", encounterId };
}

/** The encounter on the table, or null when the scene is narrative. */
export function tableSceneEncounterId(scene: TableScene): string | null {
  return scene.kind === "encounter" ? scene.encounterId : null;
}

/** Validate an untrusted (wire or persisted) table scene; null when malformed. */
export function parseTableScene(raw: unknown): TableScene | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as { kind?: unknown; encounterId?: unknown };
  if (r.kind === "narrative") return NARRATIVE_SCENE;
  if (r.kind === "encounter" && typeof r.encounterId === "string" && r.encounterId !== "") {
    return encounterScene(r.encounterId);
  }
  return null;
}

/** One-line human label: `narrative` or `encounter <id>`. */
export function describeTableScene(scene: TableScene): string {
  return scene.kind === "narrative" ? "narrative" : `encounter ${scene.encounterId}`;
}
